
import * as tf from '@tensorflow/tfjs';
import { SoundProfile } from '../../types';

/**
 * GSC HARMONY BACKEND - V3.5 (HIGH FIDELITY)
 * Optimisé pour la résonance réelle de la guitare.
 */

const HARMONIC_TEMPLATES: Record<string, number[]> = {
    // Structure: [C, C#, D, D#, E, F, F#, G, G#, A, A#, B]
    // Les valeurs sont boostées sur les fondamentales pour la robustesse.
    "5":  [1.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.6, 0.0, 0.0, 0.0, 0.0],
    maj:  [1.0, 0.0, 0.0, 0.0, 0.5, 0.0, 0.0, 0.4, 0.0, 0.0, 0.0, 0.0],
    min:  [1.0, 0.0, 0.1, 0.5, 0.0, 0.0, 0.0, 0.4, 0.0, 0.0, 0.0, 0.0],
    "7":  [1.0, 0.0, 0.0, 0.0, 0.5, 0.0, 0.0, 0.4, 0.0, 0.0, 0.3, 0.0],
    m7:   [1.0, 0.0, 0.1, 0.5, 0.0, 0.0, 0.0, 0.4, 0.0, 0.0, 0.3, 0.0]
};

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

export class TFHarmonyEngine {
    private static instance: TFHarmonyEngine;
    private _isReady = false;

    public static getInstance() {
        if (!this.instance) this.instance = new TFHarmonyEngine();
        return this.instance;
    }

    public async init() {
        if (this._isReady) return;
        await tf.ready();
        this._isReady = true;
    }

    /**
     * Nettoyage et Accentuation du Spectre
     * Utilise une fonction de puissance pour augmenter le contraste entre notes réelles et harmoniques.
     */
    private cleanGuitarSpectrum(rawChroma: number[], profile: SoundProfile = 'CLEAN'): number[] {
        const noiseFloor = profile === 'HIGH_GAIN' ? 0.12 : 0.05;
        
        // 1. Suppression du bruit de fond
        let cleaned = rawChroma.map(v => v < noiseFloor ? 0 : v);
        
        // 2. Accentuation du contraste (Sharpening)
        // En élevant à la puissance 2, on récompense les pics clairs
        cleaned = cleaned.map(v => Math.pow(v, 2));

        const max = Math.max(...cleaned, 1e-8);
        return cleaned.map(v => v / max);
    }

    public evaluateMatch(rawChroma: number[], targetChord: string, profile: SoundProfile = 'CLEAN'): { score: number } {
        const chroma = this.cleanGuitarSpectrum(rawChroma, profile);
        
        const chordName = targetChord.toUpperCase();
        const rootMatch = chordName.match(/^[A-G][#B]?/);
        const root = rootMatch ? rootMatch[0] : "C";
        
        // Normalisation des enharmoniques pour le matching
        const normalizedRoot = root.replace('Bb', 'A#').replace('Eb', 'D#').replace('Ab', 'G#').replace('Db', 'C#').replace('Gb', 'F#');
        const rootIdx = NOTE_NAMES.indexOf(normalizedRoot);
        
        let quality = "maj";
        const lowerTarget = targetChord.toLowerCase();
        if (lowerTarget.includes('min') || lowerTarget.includes('m')) quality = "min";
        if (lowerTarget.includes('7')) quality = "7";
        if (lowerTarget.includes('m7')) quality = "m7";

        const template = HARMONIC_TEMPLATES[quality] || HARMONIC_TEMPLATES["maj"]; 
        const rootOffset = rootIdx !== -1 ? rootIdx : 0;

        let dotProduct = 0;
        let templateNorm = 0;
        let chromaNorm = 0;

        for (let i = 0; i < 12; i++) {
            const templateVal = template[i];
            const actualVal = chroma[(i + rootOffset) % 12] || 0;
            
            // POIDS CRITIQUE : La fondamentale a une importance capitale (x15)
            // La tierce et quinte sont secondaires (déjà dans le template)
            let weight = (i === 0) ? 15.0 : 1.0; 
            
            dotProduct += (templateVal * actualVal * weight);
            templateNorm += (templateVal * templateVal * weight);
            chromaNorm += (actualVal * actualVal * weight);
        }

        const normFactor = (Math.sqrt(templateNorm) * Math.sqrt(chromaNorm));
        const similarity = normFactor > 1e-9 ? dotProduct / normFactor : 0;
        
        // Conversion logarithmique pour plus de générosité sur les bons matches
        // Un 0.7 de similarité brute devient un ~85% en score perçu
        const FinalScore = similarity > 0.4 
            ? Math.round(Math.pow(similarity, 0.7) * 100)
            : Math.round(similarity * 100);

        return { score: Math.max(0, Math.min(100, FinalScore)) };
    }

    public async predict(rawChroma: number[], profile: SoundProfile = 'CLEAN'): Promise<{ symbol: string, confidence: number }> {
        const chroma = this.cleanGuitarSpectrum(rawChroma, profile);
        let bestSymbol = "None";
        let maxScore = -1;

        const qualities = Object.keys(HARMONIC_TEMPLATES);
        
        for (let r = 0; r < 12; r++) {
            for (const q of qualities) {
                const symbol = `${NOTE_NAMES[r]}${q === 'maj' ? '' : q}`;
                const { score } = this.evaluateMatch(chroma, symbol, profile);
                if (score > maxScore) {
                    maxScore = score;
                    bestSymbol = symbol;
                }
            }
        }

        return { symbol: bestSymbol, confidence: maxScore / 100 };
    }
}
